import React, { useState, useCallback } from 'react';
import ClimateMap from '../ClimateMap';
import ClimateCharts from '../ClimateCharts';
import DataInventory from './DataInventory';
import AddDataPanel from './AddDataPanel';

const API = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5001`;

const LocationEditManager = ({ location, datasets, onLocationUpdated, onCancel }) => {
  const [activeTab, setActiveTab] = useState('inventory');
  const [formData, setFormData] = useState({
    location_code: location.location_code || '',
    name: location.name || '',
    description: location.description || '',
    elevation_m: location.elevation_m?.toString() || '',
    timezone: location.timezone || 'Africa/Johannesburg',
    country: location.country || 'South Africa',
    region: location.region || '',
  });
  const [message, setMessage] = useState('');
  const [selectedDatasetId, setSelectedDatasetId] = useState(datasets[0]?.id || null);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    if (name === 'location_code') {
      setFormData(prev => ({ ...prev, [name]: value.toUpperCase().slice(0, 5) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSaveDetails = async () => {
    const payload = {
      location_code: formData.location_code,
      name: formData.name,
      description: formData.description,
      latitude: location.latitude,
      longitude: location.longitude,
      elevation_m: formData.elevation_m ? parseFloat(formData.elevation_m) : null,
      timezone: formData.timezone,
      country: formData.country,
      region: formData.region,
    };
    try {
      const res = await fetch(`${API}/api/locations/${location.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        setMessage('Location details saved.');
        onLocationUpdated();
      } else {
        const err = await res.json();
        setMessage(`Error: ${err.error || 'Failed to save'}`);
      }
    } catch (e) {
      setMessage(`Error: ${e.message}`);
    }
  };

  const handleDeleteDataset = async (dsId) => {
    if (!window.confirm('Delete this climate dataset?')) return;
    try {
      await fetch(`${API}/api/datasets/${dsId}`, { method: 'DELETE' });
      if (selectedDatasetId === dsId) setSelectedDatasetId(null);
      onLocationUpdated();
    } catch (e) { console.error('Failed to delete dataset:', e); }
  };

  const handleDataAdded = useCallback(() => {
    onLocationUpdated();
    setActiveTab('inventory');
  }, [onLocationUpdated]);

  const step2Valid = formData.location_code.length === 5 && formData.name.trim() && formData.description.trim();

  const tabs = [
    { id: 'inventory', label: 'Data Inventory' },
    { id: 'add', label: 'Add Data' },
    { id: 'details', label: 'Location Details' },
    { id: 'charts', label: 'Data & Charts', disabled: datasets.length === 0 },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      {/* Header */}
      <div className="wizard-banner">
        <div className="wizard-edit-label">
          Managing: {location.location_code} &mdash; {location.name}
        </div>
        <div className="wizard-edit-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`toolbar-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
              disabled={tab.disabled}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <button className="btn-small btn-cancel" onClick={onCancel}>Close</button>
      </div>

      {message && (
        <div className={`download-msg ${message.startsWith('Error') ? 'error' : 'success'}`}>
          {message}
          <button className="btn-icon" onClick={() => setMessage('')}>&times;</button>
        </div>
      )}

      {/* Tab: Data Inventory */}
      {activeTab === 'inventory' && (
        <div style={{ padding: 16, flex: 1, overflowY: 'auto', minHeight: 0 }}>
          <DataInventory locationId={location.id} />

          {/* Dataset list with delete */}
          {datasets.length > 0 && (
            <div style={{ marginTop: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 8 }}>
                Datasets
              </div>
              <div className="dataset-selector">
                {datasets.map(ds => (
                  <button
                    key={ds.id}
                    className={`dataset-chip ${selectedDatasetId === ds.id ? 'active' : ''}`}
                    onClick={() => setSelectedDatasetId(ds.id)}
                  >
                    <span className="dataset-chip-source">{ds.source}</span>
                    <span className="dataset-chip-meta">
                      {ds.row_count?.toLocaleString()} hrs
                      {ds.year_start && ` \u00B7 ${ds.year_start}-${ds.year_end}`}
                    </span>
                    <button
                      className="btn-icon btn-danger-icon"
                      onClick={(e) => { e.stopPropagation(); handleDeleteDataset(ds.id); }}
                      title="Delete dataset"
                    >
                      &times;
                    </button>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tab: Add Data */}
      {activeTab === 'add' && (
        <div style={{ padding: 16, flex: 1, overflowY: 'auto', minHeight: 0 }}>
          <AddDataPanel
            locationId={location.id}
            latitude={location.latitude}
            longitude={location.longitude}
            onDataAdded={handleDataAdded}
          />
        </div>
      )}

      {/* Tab: Details */}
      {activeTab === 'details' && (
        <div className="wizard-form-area">
          <div className="wizard-form-split">
            <div className="wizard-form-map">
              <ClimateMap
                location={{ latitude: location.latitude, longitude: location.longitude, name: location.name }}
                sources={[]}
                onMapClick={() => {}}
                pickMode={false}
              />
            </div>
            <div className="wizard-form-fields">
              <h3>Edit Location Details</h3>
              <div className="form-group">
                <label><span className={formData.location_code.length === 5 ? 'valid-star' : 'required-star'}>*</span> Location Code (5 chars)</label>
                <input name="location_code" value={formData.location_code} onChange={handleFormChange}
                  placeholder="PPR01" maxLength={5}
                  className={formData.location_code.length === 5 ? 'field-valid' : formData.location_code ? 'field-invalid' : ''} />
              </div>
              <div className="form-group">
                <label><span className={formData.name.trim() ? 'valid-star' : 'required-star'}>*</span> Name</label>
                <input name="name" value={formData.name} onChange={handleFormChange} className={formData.name.trim() ? 'field-valid' : ''} />
              </div>
              <div className="form-group">
                <label><span className={formData.description.trim() ? 'valid-star' : 'required-star'}>*</span> Description</label>
                <input name="description" value={formData.description} onChange={handleFormChange} className={formData.description.trim() ? 'field-valid' : ''} />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Latitude</label>
                  <input type="number" value={location.latitude} readOnly className="field-valid field-readonly-ish" />
                </div>
                <div className="form-group">
                  <label>Longitude</label>
                  <input type="number" value={location.longitude} readOnly className="field-valid field-readonly-ish" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Elevation (m)</label>
                  <input name="elevation_m" type="number" value={formData.elevation_m} onChange={handleFormChange} />
                </div>
                <div className="form-group">
                  <label>Timezone</label>
                  <input name="timezone" value={formData.timezone} onChange={handleFormChange} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Country</label>
                  <input name="country" value={formData.country} onChange={handleFormChange} />
                </div>
                <div className="form-group">
                  <label>Region</label>
                  <input name="region" value={formData.region} onChange={handleFormChange} />
                </div>
              </div>
              <div className="form-actions">
                <button type="button" className="btn-primary" disabled={!step2Valid} onClick={handleSaveDetails}>
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tab: Charts */}
      {activeTab === 'charts' && datasets.length > 0 && (
        <div className="climate-data-area">
          <div className="dataset-selector">
            {datasets.map(ds => (
              <button
                key={ds.id}
                className={`dataset-chip ${selectedDatasetId === ds.id ? 'active' : ''}`}
                onClick={() => setSelectedDatasetId(ds.id)}
              >
                <span className="dataset-chip-source">{ds.source}</span>
                <span className="dataset-chip-meta">
                  {ds.row_count?.toLocaleString()} hrs
                  {ds.year_start && ` \u00B7 ${ds.year_start}-${ds.year_end}`}
                </span>
              </button>
            ))}
          </div>
          <ClimateCharts datasetId={selectedDatasetId} />
        </div>
      )}
    </div>
  );
};

export default LocationEditManager;
